import React from 'react';
import {
    Card,
    CardHeader,
    Divider,
    Table,
    TableBody,
    makeStyles,
    IconButton,
    Box,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ILeadInfo, TValue, TUpdatedValues } from './types';
import { CapitalizeNames } from 'src/utils/capitalize';
import { ExternalLink } from 'react-feather';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { LeadFromRow } from '../../tables/tableRows/LeadFromRow';
import useAuth from 'src/hooks/useAuth';
import useLead from 'src/hooks/useLead';
import {
    validateVehicleDeliveryDate,
    validateEditableVehicleDeliveryDate,
} from '../helpers';
import { STATUSES } from 'src/constants/mongo/status';
import { tableRowLeadInfoFactory } from '../factories';

const useStyles = makeStyles(() => ({
    paper: {
        overflow: 'scroll',
    },
    margin: {
        marginBottom: '1em',
    },
    icon: {
        marginLeft: 10,
    },
}));

const DATE_FORMAT = 'DD MMMM YYYY - hh:mm a';

export const FollowInfo = ({ lead, loading, ...props }: ILeadInfo) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { updateLead } = useLead();
    let history = useHistory();

    const handleGoCalendar = async (appoinmentId: string) =>
        history.push(`/calendar?appoinment=${appoinmentId}`);

    const handleSave = (values: TUpdatedValues) => updateLead(values, lead._id);

    const SUBSTATUS_SOLD_SUCCESS =
        STATUSES.CLOSURE_INFORMATION.SUBSTATUSES.SOLD.ID;
    const isSoldStatus = lead.substatus?._id === SUBSTATUS_SOLD_SUCCESS;

    const VALUES: TValue[] = [
        {
            header: t('Leads.Campaign'),
            text: lead.campaign
                ? CapitalizeNames(lead?.campaign?.name)
                : t('Messages.NoneA'),
        },
        {
            header: t('Leads.Assigned'),
            text: CapitalizeNames(lead?.agent?.name),
        },
        {
            header: t('Leads.Date'),
            text: moment(lead?.createdAt).format(DATE_FORMAT),
        },

        {
            header: t('Leads.AssignedDate'),
            text: lead.assignedDate
                ? moment(lead?.assignedDate).format(DATE_FORMAT)
                : t('Messages.NoneA'),
        },
        {
            header: t('Leads.FirstTask'),
            text: lead.firstTask
                ? moment(lead?.firstTask).format(DATE_FORMAT)
                : t('Messages.NoneA'),
        },
        {
            header: t('Leads.FirstContact'),
            text: lead.firstContact
                ? moment(lead?.firstContact).format(DATE_FORMAT)
                : t('Messages.NoneA'),
        },
        {
            header: t('Leads.Task'),
            text: lead.lastTask
                ? moment(lead?.lastTask).format(DATE_FORMAT)
                : t('Messages.NoneA'),
        },
        {
            header: t('Leads.AppointmentDate'),
            text: lead.appointmentDate ? (
                <Box display="flex" alignItems="center">
                    {moment(lead?.appointmentDate).format(DATE_FORMAT)}
                    <IconButton
                        className={classes.icon}
                        onClick={() =>
                            handleGoCalendar(
                                lead.appointments[lead.appointments.length - 1]
                                    ._id
                            )
                        }
                    >
                        <ExternalLink />
                    </IconButton>
                </Box>
            ) : (
                t('Messages.NoApp')
            ),
        },
        {
            header: t('Leads.Attend'),
            text:
                lead?.visits?.length > 0
                    ? t('Messages.Visit')
                    : t('Messages.NoVisit'),
        },
        {
            header: t('Leads.Closure'),
            text: lead?.closureDate
                ? moment(lead?.closureDate).format(DATE_FORMAT)
                : t('Messages.NoneA'),
        },
        {
            header: t('Leads.Updated'),
            text: lead.updatedAt
                ? moment(lead?.updatedAt).format(DATE_FORMAT)
                : t('Messages.NoneA'),
        },
        ...(isSoldStatus
            ? [
                  {
                      header: t('Leads.VehicleDeliveryDate'),
                      text: lead?.vehicleDeliveryDate
                          ? moment(lead?.vehicleDeliveryDate).format(
                                DATE_FORMAT
                            )
                          : t('Messages.NoneA'),
                      editable: validateEditableVehicleDeliveryDate({
                          lead,
                          user,
                      }),
                      field: 'vehicleDeliveryDate',
                      validate: validateVehicleDeliveryDate,
                  },
              ]
            : []),
        {
            header: t('Leads.CreatedBy'),
            text: CapitalizeNames(lead?.createdBy?.name),
        },
    ];

    return (
        <Card className={classes.paper} {...props}>
            <CardHeader title={t('Leads.InfoFollow')} />
            <Divider />
            <Table className={classes.margin}>
                <TableBody>
                    {VALUES.map((item, key) =>
                        tableRowLeadInfoFactory({
                            item,
                            loading,
                            handleSave,
                            key,
                        })
                    )}
                    <LeadFromRow lead={lead} user={user} />
                </TableBody>
            </Table>
        </Card>
    );
};
